import type {
  PostNordConditionalDeliveryType,
  PostNordCountry,
  PostNordDeliveryType
} from 'ecosystem'
import { isNullable } from 'shared-utils'
import { postnordDeliveryOptionsLimits } from '../constants'

/**
 * Generates filtered PostNord delivery types based on conditional rules
 * @param conditionalDeliveryTypes - Array of delivery types with conditions
 * @param cartTotalAmount - Cart total amount for filtering
 * @param countryCode - Country code for filtering
 * @param parcelWeight - Total weight of the parcel in kilograms for weight limit filtering
 * @returns Array of delivery types that match the conditions
 */
export const generatePostnordDeliveryTypes = (
  conditionalDeliveryTypes: PostNordConditionalDeliveryType[],
  cartTotalAmount: number,
  countryCode: PostNordCountry,
  parcelWeight: number
): PostNordDeliveryType[] => {
  const availableDeliveryTypes: PostNordDeliveryType[] = []

  for (const deliveryType of conditionalDeliveryTypes) {
    // Check weight limits first for this delivery type
    const weightLimit = postnordDeliveryOptionsLimits[deliveryType.type]?.maxWeight

    // Skip delivery type if parcel weight exceeds limits
    if (!isNullable(weightLimit) && parcelWeight > weightLimit) {
      continue
    }

    // If no conditions or empty conditions array, always include this delivery type
    if (!deliveryType.conditions || deliveryType.conditions.length === 0) {
      availableDeliveryTypes.push(deliveryType.type)
      continue
    }

    // Find all countries explicitly mentioned in non-null conditions
    const explicitCountries = new Set<string>()
    deliveryType.conditions.forEach((condition) => {
      if (!isNullable(condition.countries)) {
        condition.countries.forEach((country) => explicitCountries.add(country))
      }
    })

    // Determine which conditions to evaluate based on country
    const applicableConditions = deliveryType.conditions.filter((condition) => {
      if (isNullable(condition.countries)) {
        // null conditions apply only if the country is NOT explicitly mentioned elsewhere
        return !explicitCountries.has(countryCode)
      }

      // explicit conditions apply only if the country is in the list
      return condition.countries.includes(countryCode)
    })

    // Check if any applicable condition matches the cart amount
    const hasMatchingCondition = applicableConditions.some((condition) => {
      if (isNullable(condition.orderAmount)) {
        return true // If no order amount condition, it matches
      }
      const { from, to } = condition.orderAmount
      return cartTotalAmount >= from && (isNullable(to) || cartTotalAmount < to)
    })

    if (hasMatchingCondition) {
      availableDeliveryTypes.push(deliveryType.type)
    }
  }

  return availableDeliveryTypes
}
